import DatePicker from "@/components/DatePicker/index";
import DSInput from "@/componentsDS/Input";
import DSInputDropdown from "@/componentsDS/InputDropdown";
import { validatePitstopURL } from "@/utils/validate-email";
import { sanitizePitstopName, sanitizePitstopUrl, validatePitstopName } from "@/utils/validate-pitstop";
import validateUEN from "@/utils/validateUEN";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Controller } from 'react-hook-form';

const CompanyInfo = ({
	errors,
	register,
	watch,
	setValue,
	infoItems,
	companyInfo,
	setCompanyInfo,
	defaultDomain,
	control,
	isFilledWithMyInfo,
	currentProfile,
	trigger
}) => {
	const [companyType, setCompanyType] = useState("");
	const [countryOfIncorporation, setCountryOfIncorporation] = useState("");

	const [isPitstopNameModified, setIsPitstopNameModified] = useState(false);
	const [isPitstopUrlModified, setIsPitstopUrlModified] = useState(false);
	const { company, pitstopUrl, pitstopName, uen, countryOfIncorporation: coi } = watch([
		"company",
		"pitstopUrl",
		"pitstopName",
		"uen",
		"countryOfIncorporation"
	]);

	useEffect(() => {
		if (uen && uen !== uen.toUpperCase()) {
			setValue("uen", uen.toUpperCase(), { shouldValidate: true });
		}
	}, [uen, setValue]);

	useEffect(() => {
		if (!isPitstopNameModified && company) {
			const pitstop = sanitizePitstopName(company);
			setValue("pitstopName", pitstop, { shouldValidate: true });
		}
	}, [company]);


	useEffect(() => {
		if (!isPitstopUrlModified && pitstopName) {
			const url = sanitizePitstopUrl(pitstopName);
			setValue("pitstopUrl", url, { shouldValidate: true });
		}
	}, [pitstopName]);

	useEffect(() => {
		if(coi) {
			trigger("uen")
		}
	}, [coi])

	return (
		<>
			<div className="login-box__section-title-buildex">
				Company Information
			</div>
			<div className="login-box__form__cols--buildex">
				<div className="login-box__form__col">
					<DSInput
						name="company"
						label="Company Name"
						placeholder="Enter company name"
						errorMessage={errors.company?.message}
						required={true}
						id="company-input"
						fullWidth={true}
						value={company}
						disabled={isFilledWithMyInfo}
						register={register({
							required: "This field is mandatory",
							minLength: {
								value: 3,
								message: "Please enter a valid company name",
							},
						})}
						setValue={setValue}
					/>
				</div>
				<div className="login-box__form__col">
					<DSInput
						name="pitstopName"
						label="Pitstop Name"
						placeholder="Enter pitstop name"
						errorMessage={errors.pitstopName?.message}
						required={true}
						id="pitstopName-input"
						fullWidth={true}
						setValue={setValue}
						value={pitstopName}
						register={register({
							required: "This field is mandatory",
							minLength: {
								value: 3,
								message: "Please enter a valid Pitstop Name",
							},
							validate: {
								checkValidPistopShortName: (v) =>
									validatePitstopName(v.trim()) ||
									"Please Provide valid Pitstop Name",
							},
						})}
						keyPress={(e) =>
							!isPitstopNameModified && setIsPitstopNameModified(true)
						}
					/>
				</div>
				<div className="login-box__form__col">
					<DSInput
						name="pitstopUrl"
						label="Pitstop URL"
						placeholder="Enter pitstop url"
						errorMessage={errors.pitstopUrl?.message}
						required={true}
						id="pitstopUrl-input"
						fullWidth={true}
						setValue={setValue}
						value={pitstopUrl}
						register={register({
							required: "This field is mandatory",
							minLength: {
								value: 3,
								message: "Please enter a valid Pitstop URL",
							},
							maxLength: {
								value: 37,
								message: "Pitstop URL cannot exceed 37 characters",
							},
							validate: {
								checkEmail: (v) =>
									validatePitstopURL(v) ||
									"Please enter a valid Pitstop URL",
							},
						})}
						pipeText={pitstopUrl?.trim().toLowerCase() + defaultDomain}
						keyPress={(e) =>
							!isPitstopUrlModified && setIsPitstopUrlModified(true)
						}
					/>
				</div>
				<div className="login-box__form__col login-box__form__col--half">
					<DSInput
						name="uen"
						label="Company Registration No / UEN"
						placeholder="Enter Unique Entity Number"
						errorMessage={errors.uen?.message}
						required={true}
						id="uen"
						fullWidth={true}
						setValue={setValue}
						disabled={isFilledWithMyInfo}
						value={uen}
						register={register({
							required: "This field is mandatory",
							validate: {
								checkUEN: (v) =>
									validateUEN(v, coi ?? "SG") ||
									"Please enter a valid UEN",
							},
						})}
					/>
				</div>
				<div className="login-box__form__col login-box__form__col--half login-box-buildex__dropdown">
					<Controller
						control={control}
						name="companyType"
						render={({ onChange: controllerOnChange }) => {
							return (
								<DSInputDropdown
									name="companyType"
									label="Company Type"
									placeholder="Select Option"
									errorMessage={errors.companyType?.message}
									id="company-type"
									dropdownOption={
										infoItems?.companyType ?? []
									}
									size="default"
									fullWidth={true}
									disabled={isFilledWithMyInfo && currentProfile.organization.companyType}
									value={currentProfile.organization.companyType?.desc ?? companyType}
									onChange={setCompanyType}
									onSelectDropdownItem={(item) => {
										controllerOnChange(item[0]?.id);
									}}
								/>
							);
						}}
					/>
				</div>
				<div className="login-box__form__col login-box__form__col--half">
					<div className="DS__input-label">Date of Incorporation</div>
					<div>
						<DatePicker
							selectDate={companyInfo.dateOfIncorporation}
							setSelectDate={(date) =>
								setCompanyInfo((prev) => ({
									...prev,
									dateOfIncorporation: date,
								}))
							}
							id="date_of_incorporation"
							dateFormat="dd-MM-yyyy"
							isClearable={!isFilledWithMyInfo}
							readOnly={isFilledWithMyInfo}
							showMonthDropdown
							showYearDropdown
							dropdownMode="select"
							maxDate={new Date()}
							placeHolderText={"DD/MM/YYYY"}
							showTimeSelect={false}
							disable={isFilledWithMyInfo}
							addClass={
								isFilledWithMyInfo
									? "login-box-buildex__input__disabled"
									: ""
							}
						/>
					</div>
				</div>
				<div className="login-box__form__col login-box__form__col--half login-box-buildex__dropdown">
					<Controller
						name="countryOfIncorporation"
						control={control}
						rules={{ required: "This field is required" }}
						render={({ onChange: controllerOnChange }) => {
							return (
								<DSInputDropdown
									name="countryOfIncorporation"
									label="Country of Incorporation"
									placeholder="Select Option"
									errorMessage={
										errors.countryOfIncorporation?.message
									}
									id="company-of-incorporation"
									dropdownOption={
										infoItems?.countryOfIncorp ?? []
									}
									size="default"
									required={true}
									fullWidth={true}
									disabled={isFilledWithMyInfo && currentProfile.organization.countryOfIncorporation}
									value={currentProfile.organization.countryOfIncorporation?.desc ?? countryOfIncorporation}
									onChange={setCountryOfIncorporation}
									onSelectDropdownItem={(item) => {
										controllerOnChange(item[0]?.id);
									}}
								/>
							);
						}}
					/>
				</div>
				<div className="login-box__form__col login-box__registered-address">
					<div className="DS__input-label">Registered Address</div>
					<textarea
						className="login-box__registered-address__textarea"
						rows={4}
						placeholder="Enter address"
						name="registeredAddress"
						ref={register({
							required: false,
						})}
						readOnly={isFilledWithMyInfo && currentProfile.organization?.registeredAddress}
					/>
				</div>
			</div>

		</>
	);
}


export default CompanyInfo;

CompanyInfo.propTypes = {
	errors: PropTypes.object,
	register: PropTypes.func,
	watch: PropTypes.func,
	setValue: PropTypes.func,
	infoItems: PropTypes.object,
	companyInfo: PropTypes.object,
	setCompanyInfo: PropTypes.func,
	defaultDomain: PropTypes.string,
	control: PropTypes.object,
	isFilledWithMyInfo: PropTypes.bool,
	currentProfile: PropTypes.object,
	trigger: PropTypes.func
}

